// BillingForm.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Form, Dropdown, Loader, Button } from "semantic-ui-react";
import { useFormik } from "formik";
import { BillingsAPI } from "../../../../api/billingsAPI";
import { User } from "../../../../api"; // API para listar usuarios
import { PlanesAPI } from "../../../../api/planesAPI";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./BillingForm.form";
import { STATUS_OPTIONS } from "./statusOptions";
import "./BillingForm.scss";

const billingController = new BillingsAPI();
const userController = new User();
const planActivationsAPI = new PlanesAPI();

const ROLE_FILTER_OPTIONS = [
  { key: "all", value: "all", text: "Todos los roles" },
  { key: "client", value: "client", text: "Cliente" },
  { key: "partner", value: "partner", text: "Partner" },
  { key: "teacher", value: "teacher", text: "Profesor" },
  { key: "institution", value: "institution", text: "Institución" },
];

export function BillingForm(props) {
  const { billing, onClose, onReload } = props;
  const isEditing = !!billing;
  const { accessToken, user } = useAuth();
  const billingData = billing?.billings || null;

  // Step: 1 = Factura; 2 = Activaciones (solo para usuarios partner)
  const [step, setStep] = useState(1);
  // Para creación, se guarda el doc de factura creado (si es partner)
  //const [createdBilling, setCreatedBilling] = useState(null);

  // Estados locales para selección de usuario (solo en step 1, si es creación)
  const [roleFilter, setRoleFilter] = useState("all");
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Estados para activaciones (step 2)
  const [activationArray, setActivationArray] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [planOptions, setPlanOptions] = useState([]);

  // Cargar planes usando la instancia de PlanesAPI (no billingController)
  const loadPlans = useCallback(async () => {
    try {
      const response = await planActivationsAPI.getPlans(accessToken, {
        page: 1,
        limit: 100,
      });
      if (response && response.plans) {
        const options = response.plans.map((plan) => ({
          key: plan._id,
          text: plan.title,
          value: plan._id,
        }));
        setPlanOptions(options);
      } else {
        setPlanOptions([]);
        console.error("No se encontraron planes en la respuesta");
      }
    } catch (error) {
      console.error("Error al cargar planes:", error);
    }
  }, [accessToken]); // <-- Dependencia de useCallback

  useEffect(() => {
    if (step === 2) {
      loadPlans();
    }
  }, [step, loadPlans]);

  // Formik para step 1 (factura)
  const formik = useFormik({
    initialValues: initialValues(billingData),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        if (isEditing) {
          await billingController.updateBilling(
            accessToken,
            billing._id,
            formValue,
          );
          onReload();
          onClose();
        } else {
          if (!formValue.userId || !formValue.senderRole) {
            alert("Debes seleccionar un usuario para la factura.");
            return;
          }
          // Crear la factura
          await billingController.createBilling(
            accessToken,
            formValue.userId,
            formValue,
          );
          // Si el usuario seleccionado tiene rol partner, pasamos al step 2
          if (formValue.senderRole.toLowerCase() === "partner") {
            //setCreatedBilling(result.billings);
            setStep(2);
          } else {
            onReload();
            onClose();
          }
        }
      } catch (error) {
        console.error("Error al enviar el formulario de factura:", error);
      }
    },
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const response = await userController.getUsers(accessToken, true);
      if (!response?.users) {
        setUserList([]);
        return;
      }
      let finalList = response.users;
      if (roleFilter !== "all") {
        finalList = finalList.filter((u) => u.role === roleFilter);
      }
      setUserList(finalList);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      setUserList([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [accessToken, roleFilter]);

  // Cargar usuarios para creación, solo en step 1
  useEffect(() => {
    if (!isEditing && step === 1) {
      loadUsers();
    }
  }, [isEditing, step, loadUsers]);

  const userOptions = userList.map((u) => ({
    key: u._id,
    value: u._id,
    text: `${u.firstname} ${u.lastname} (${u.email})`,
    role: u.role,
  }));

  const handleUserChange = (_, { value }) => {
    const userSel = userList.find((usr) => usr._id === value);
    if (userSel) {
      formik.setFieldValue("userId", userSel._id);
      // Forzamos a minúsculas para la comparación
      formik.setFieldValue("senderRole", userSel.role.toLowerCase());
    }
  };

  // Render Step 1: Formulario de Factura
  const renderStep1 = () => (
    <Form className="billing-form" onSubmit={formik.handleSubmit}>
      {!isEditing && (
        <>
          <Form.Field>
            <label>Filtrar Role</label>
            <Dropdown
              fluid
              selection
              options={ROLE_FILTER_OPTIONS}
              value={roleFilter}
              onChange={(_, { value }) => setRoleFilter(value)}
            />
          </Form.Field>

          <Form.Field>
            <label>Seleccionar Usuario</label>
            {loadingUsers ? (
              <Loader active inline="centered" />
            ) : (
              <Dropdown
                fluid
                selection
                placeholder="Selecciona un usuario"
                options={userOptions}
                onChange={handleUserChange}
              />
            )}
          </Form.Field>
        </>
      )}
      <Form.Group widths="equal">
        <Form.Input
          label="Número de Factura"
          name="billingNumber"
          onChange={formik.handleChange}
          value={formik.values.billingNumber}
          error={formik.errors.billingNumber}
        />
        <Form.Input
          label="Total"
          name="billingtotal"
          onChange={formik.handleChange}
          value={formik.values.billingtotal}
          error={formik.errors.billingtotal}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label="Cuota Partner"
          name="billingpartner"
          onChange={formik.handleChange}
          value={formik.values.billingpartner}
          error={formik.errors.billingpartner}
        />
        <Form.Select
          fluid
          label="Estado"
          name="status"
          options={STATUS_OPTIONS}
          value={formik.values.status}
          onChange={(_, { value }) => formik.setFieldValue("status", value)}
          error={formik.errors.status}
        />
      </Form.Group>

      <Form.TextArea
        label="Descripción"
        name="description"
        onChange={formik.handleChange}
        value={formik.values.description}
        error={formik.errors.description}
      />

      <Form.Field>
        <label>Factura Cliente (Opcional)</label>
        <input
          type="file"
          onChange={(e) => formik.setFieldValue("file1", e.target.files[0])}
          accept="application/pdf,image/*"
        />
      </Form.Field>

      <Form.Field>
        <label>Factura Proveedor (Opcional)</label>
        <input
          type="file"
          onChange={(e) => formik.setFieldValue("file2", e.target.files[0])}
          accept="application/pdf,image/*"
        />
      </Form.Field>

      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {isEditing
          ? "Actualizar Factura"
          : formik.values.senderRole === "partner"
            ? "Crear y Siguiente"
            : "Crear Factura"}
      </Form.Button>
      {isEditing && formik.values.senderRole === "partner" && (
        <Button type="button" secondary onClick={() => setStep(2)}>
          Editar Activaciones
        </Button>
      )}
    </Form>
  );

  // Render Step 2: Formulario para Activar Planes
  const renderStep2 = () => (
    <div className="billing-form-step2">
      <h3>Activar Planes para el Usuario Partner</h3>
      <Form>
        <Form.Field>
          <label>Seleccionar Plan</label>
          <Dropdown
            fluid
            selection
            placeholder="Selecciona un plan"
            options={planOptions}
            onChange={(_, { value }) => setSelectedPlan(value)}
            value={selectedPlan}
          />
        </Form.Field>

        <Form.Field>
          <label>Cantidad</label>
          <input
            type="number"
            placeholder="Cantidad"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </Form.Field>

        <Form.Button type="button" primary onClick={handleAddActivation}>
          Agregar Activación
        </Form.Button>
      </Form>
      {activationArray.length > 0 && (
        <div className="activation-list">
          <h4>Activaciones Agregadas:</h4>
          <ul>
            {activationArray.map((act, index) => (
              <li key={index}>
                Plan: {act.plan} - Cantidad: {act.quantity}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Form.Button
        type="button"
        color="green"
        onClick={handleFinishActivations}
      >
        Enviar Activaciones
      </Form.Button>
    </div>
  );

  const handleAddActivation = () => {
    if (!selectedPlan) {
      alert("Debes seleccionar un plan.");
      return;
    }
    if (!quantity || isNaN(quantity) || Number(quantity) <= 0) {
      alert("Debes ingresar una cantidad válida.");
      return;
    }
    const newActivation = {
      plan: selectedPlan,
      quantity: Number(quantity),
    };
    setActivationArray([...activationArray, newActivation]);
    setSelectedPlan(null);
    setQuantity("");
  };

  const handleFinishActivations = async () => {
    try {
      if (!formik.values.userId) {
        alert("No se ha seleccionado un usuario partner.");
        return;
      }
      await planActivationsAPI.addActivations(
        accessToken,
        user.user._id, // admin id
        formik.values.userId, // partner id
        { activations: activationArray },
      );
      alert("Activaciones creadas correctamente.");
      onReload();
      onClose();
      // Reiniciamos el step en caso de que se vuelva a usar el formulario
      setStep(1);
    } catch (error) {
      console.error("Error al enviar activaciones:", error);
      alert("Error al enviar activaciones.");
    }
  };

  return (
    <div className="billing-form-container">
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
}
